import {
  Box,
  Button,
  Inline,
  Switcher,
  Table,
  useViewport,
} from '@omnipresentgroup/design-system';
import { usePayrollProviders } from '@omnipresentgroup/domains';
import { COUNTRY_CODE_PARAM, CountrySelector } from '@omnipresentgroup/shared';
import type { PayrollProvider } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { PayrollProviderStatusSelector } from './components';
import { PAYROLL_PROVIDER_STATUS_PARAM } from './constants';
import { ColumnHeaders, usePayrollProvidersTableConfig } from './hooks';

const EMPTY_ARRAY: PayrollProvider[] = [];
const LOADING_ARRAY = Array.from({ length: 10 }, () => ({}) as PayrollProvider);

const DEFAULT_SORT: SortingState = [
  {
    id: 'name',
    desc: false,
  },
];

type Props = {
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  columnsOverride?: ColumnDef<PayrollProvider, unknown>[];
  optionsOverride?: Omit<TableOptions<PayrollProvider>, 'data' | 'columns'>;
  onRowClick?: (row: PayrollProvider) => void;
  filters?: Partial<Record<'countryCode' | 'status', boolean>>;
  countryCode?: string;
  onEditClick?: (id: string) => void;
};

export const PayrollProvidersList = ({
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  onRowClick,
  filters,
  onEditClick,
}: Props) => {
  const [searchParameters, setSearchParameters] = useSearchParams();

  const countryCode = new URLSearchParams(searchParameters).get(
    COUNTRY_CODE_PARAM,
  ) as string;

  const payrollProviderStatus = new URLSearchParams(searchParameters).get(
    PAYROLL_PROVIDER_STATUS_PARAM,
  ) as string;

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const tableOptions: Omit<
    TableOptions<PayrollProvider>,
    'data' | 'columns'
  > = useMemo(
    () => ({
      state: {
        columnVisibility: columnVisibilityState,
        columnOrder,
        sorting,
        columnFilters,
      },
      getCoreRowModel: getCoreRowModel(),
      onColumnVisibilityChange: setColumnVisibilityState,
      onColumnOrderChange: setColumnOrder,
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel(),
      ...optionsOverride,
    }),
    [
      columnFilters,
      columnOrder,
      columnVisibilityState,
      optionsOverride,
      sorting,
    ],
  );

  const { columns } = usePayrollProvidersTableConfig({
    columnsOverride,
    onEditClick,
  });

  const isActive = useMemo(() => {
    switch (payrollProviderStatus) {
      case 'active': {
        return true;
      }
      case 'inactive': {
        return false;
      }
      default: {
        // eslint-disable-next-line unicorn/no-useless-undefined
        return undefined;
      }
    }
  }, [payrollProviderStatus]);

  const { data: payrollProviders, isLoading } = usePayrollProviders({
    isActive: isActive,
  });

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : payrollProviders || EMPTY_ARRAY,
    columns,
    getRowId: (row: PayrollProvider) => row.id,
    ...tableOptions,
  });

  // Update column filters based on search parameters
  useEffect(() => {
    setColumnFilters((old) => [
      ...old.filter((filter) => filter.id !== 'countryCode'),
      ...(countryCode
        ? [
            {
              id: 'countryCode',
              value: countryCode,
            },
          ]
        : []),
    ]);
  }, [countryCode, setColumnFilters]);

  const Filters = () => {
    const { isPhone, isTablet } = useViewport();
    const isStacked = useMemo(() => isPhone || isTablet, [isPhone, isTablet]);
    return (
      <Switcher
        isStacked={isStacked}
        css={{
          flexWrap: 'wrap',
        }}
      >
        {filters?.countryCode && (
          <Box minW={isStacked ? undefined : 270}>
            <CountrySelector label={ColumnHeaders.countryCode} />
          </Box>
        )}
        {filters?.status && (
          <Box minW={isStacked ? undefined : 200}>
            <PayrollProviderStatusSelector label={ColumnHeaders.isActive} />
          </Box>
        )}
        {filters && Object.keys(filters).length > 0 && (
          <Inline align="end" justify="end">
            <Button
              variant="tertiary"
              icon="FilterOff"
              onClick={() => {
                setSearchParameters((parameters) => {
                  parameters.delete(COUNTRY_CODE_PARAM);
                  parameters.delete(PAYROLL_PROVIDER_STATUS_PARAM);
                  return parameters;
                });
              }}
            >
              Clear
            </Button>
          </Inline>
        )}
      </Switcher>
    );
  };

  return (
    <Table
      id="payroll-providers-list"
      table={table}
      loading={isLoading}
      usePagination
      useSorting
      filterSection={filters && <Filters />}
      emptyStateProps={{
        id: 'payroll-providers-empty-list',
        title: 'No payroll providers found',
      }}
      onRowClick={
        onRowClick
          ? (row) => {
              onRowClick(row);
            }
          : undefined
      }
    />
  );
};
